import React, { useEffect } from 'react';
import { useMutation } from 'hooks/apollo';

import { FormattedMessage, injectIntl } from 'react-intl';
import { Checkbox, Col, Divider, Form, Input, Row, Select, Spin } from 'antd';
import InfoToolip from 'components/InputInfoCircle';

import globalMessages from 'translations/messages';

import SurveyCategorySelect from 'components/SurveyCategorySelect';
import useGlobalContext from 'hooks/useGlobalContext';
import { SurveyType } from 'shared/services/prisma/types';
import LanguageSelect from 'components/LanguageSelect';
import updateSettingsMutation from './updateSurveySettings.gql';
import createMutation from './createSurvey.gql';

import messages from './messages';

const { Option } = Select;

const EditSettings = ({
  intl: { formatMessage: $t },
  form,
  onDone,
  survey,
  customer,
}) => {
  const [updateSurveySettings] = useMutation(updateSettingsMutation);
  const [createSurvey, { loading: creating }] = useMutation(createMutation);
  const { message } = useGlobalContext();

  useEffect(() => form.resetFields(), [survey?.id]);

  const onFinish = async (values) => {
    const input = values;

    try {
      let createdId;
      if (!survey) {
        input.customerId = customer.id;

        const { data: { created } } = await createSurvey({ variables: { input } });
        createdId = created.id;
      } else {
        input.surveyId = survey.id;

        await updateSurveySettings({ variables: { input } });
      }

      message.success($t(messages.saveSuccess));
      onDone(createdId);
    } catch (error) {
      message.error($t(messages.saveError));
    }
  };

  function getInitialValues() {
    if (survey) {
      return {
        title: survey.title,
        customerAlias: survey.customerAlias,
        type: survey.type,
        language: survey.language,
        showProgress: survey.settings.showProgress,
        clientPanel: survey.settings.clientPanel,
        categoryIds: survey.categories.map(cat => cat.id),
        groupTitle: survey.languageParent?.title,
      };
    }

    return {
      type: SurveyType.General,
    };
  }

  return (
    <Spin spinning={creating} size="large">
      <Form layout="vertical" onFinish={onFinish} form={form} initialValues={getInitialValues()}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label={<FormattedMessage {...messages.titleLabel} />}
              rules={[{ required: true, message: $t(messages.titleRequired) }]}
            >
              <Input placeholder={$t(messages.titlePlaceholder)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customerAlias"
              label={(
                <>
                  <FormattedMessage {...messages.customerAliasLabel} />
                  <InfoToolip placement="right" title={<FormattedMessage {...messages.customerAliasTooltip} />} />
                </>
              )}
            >
              <Input placeholder={customer.alias || customer.name} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="type"
              label={<FormattedMessage {...messages.type} />}
              rules={[{ required: true, message: $t(messages.typeRequired) }]}
            >
              <Select placeholder={$t(messages.typePlaceholder)}>
                {Object.values(SurveyType).map(type => (
                  <Option key={type} value={type}>
                    <FormattedMessage {...globalMessages[type.toLowerCase()]} />
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="language"
              label={<FormattedMessage {...messages.languageLabel} />}
              rules={[{ required: true, message: $t(messages.languageRequired) }]}
            >
              <LanguageSelect />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="categoryIds"
              label={<FormattedMessage {...messages.categoriesLabel} />}
            >
              <SurveyCategorySelect mode="multiple" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={4}>
            <Form.Item name="clientPanel" valuePropName="checked">
              <Checkbox>
                <FormattedMessage {...messages.enableClientPanel} />
                <span style={{ marginLeft: 8 }}>
                  <InfoToolip placement="right" title={<FormattedMessage {...messages.clientPanelTooltip} />} />
                </span>
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="showProgress" valuePropName="checked">
              <Checkbox><FormattedMessage {...messages.showProgress} /></Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {survey?.languageParent && (
        <>
          <Divider orientation="left">Group settings</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="groupTitle" label={<FormattedMessage {...messages.groupTitleLabel} />}>
                <Input placeholder={$t(messages.groupTitlePlaceholder)} />
              </Form.Item>
            </Col>
          </Row>
        </>
        )}
      </Form>
    </Spin>
  );
};

export default injectIntl(EditSettings);
